// API service for communicating with the Azure Function App backend
import { acquireTokenSilent } from '../config/msalConfig';
import { srFetch } from '../utils/srErrors';

export interface ExtractedField {
  field_name: string;
  field_value: string;
  confidence: 'high' | 'medium' | 'low';
  extraction_method: string;
  created_at: string | null;
}

export function isSrReferral(referral: Pick<Referral, 'source' | 'is_sr_referral'>): boolean {
  return referral.source === 'sr' || referral.is_sr_referral === true;
}

export type SRCompleteResponseAction =
  | 'none'
  | 'acknowledge'
  | 'decline'
  | 'message';

export interface SRCompleteResponse {
  action: SRCompleteResponseAction;
  body?: string;
}

export interface SRMessageResult {
  attempted?: boolean;
  sent?: boolean;
  error?: string;
  message_id?: string;
  msg_type?: number;
}

export interface UpdateReferralStatusResult {
  referral?: Referral;
  sr_message?: SRMessageResult;
  success?: boolean;
  status?: string;
  error?: string;
  message?: string;
  error_code?: string;
  missing_fields?: string[];
  missing_field_count?: number;
  blocker_code?: string;
  required_action?: string;
}

export function referralHasMissingFields(referral: Pick<Referral, 'missing_fields'>): boolean {
  const missingFields = referral.missing_fields;
  if (!missingFields) {
    return false;
  }
  if (Array.isArray(missingFields)) {
    return missingFields.length > 0;
  }
  if (typeof missingFields === 'string') {
    try {
      const parsed = JSON.parse(missingFields);
      return Array.isArray(parsed) ? parsed.length > 0 : missingFields.trim().length > 0;
    } catch {
      return missingFields.trim().length > 0;
    }
  }
  return false;
}

export function canMarkCompleteReferral(referral: Referral): boolean {
  if (isSrReferral(referral) && isSrReferralCancelled(referral)) {
    return false;
  }

  return (
    referral.status === 'ready_for_indici' ||
    (referral.status === 'awaiting_reply' && referral.is_complete === true)
  );
}

export function isSrReferralCancelled(
  referral: Pick<Referral, 'status' | 'sr_cancelled_at'>,
): boolean {
  return String(referral.status || '').toLowerCase() === 'cancelled' || Boolean(referral.sr_cancelled_at);
}

export interface Referral {
  id: string;
  source?: string;
  is_sr_referral?: boolean;
  is_simulation?: boolean;
  patient_first_name: string;
  patient_last_name: string;
  patient_dob: string | null;
  nhi_number: string | null;
  acc_claim_number: string | null;
  contact_phone: string | null;
  contact_email: string | null;
  clinical_notes: string | null;
  status: string;
  priority: string | null;
  referral_type: string | null;
  sub_status: string | null;
  body_side: string | null;
  body_part: string | null;
  date_of_injury: string | null;
  mechanism_of_injury: string | null;
  is_acc_related: boolean | null;
  acc_number_validated: boolean | null;  // Keep for backwards compatibility
  acc_validation_status_current?: string | null;  // Latest validation status from identifier_validations table
  acc_validation_reason_message?: string | null;  // Reason message explaining validation result
  acc_validation_metadata?: ACCValidationMetadata | null;  // ACC API response metadata (coverStatus, diagnoses, etc.)
  declined_acc_approved_at?: string | null;
  declined_acc_approved_by_user_id?: string | null;
  declined_acc_approval_note?: string | null;
  imaging_performed: boolean | null;
  imaging_facility: string | null;
  referring_provider_name: string | null;
  referring_provider_org: string | null;
  referring_provider_email: string | null;
  referring_provider_edi: string | null;
  funder: string | null;
  received_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  attachment_count: number;
  attachments?: Attachment[];
  is_complete?: boolean;
  missing_fields?: string[] | string | null;
  assigned_to_user_id?: string | null;
  is_escalated?: boolean;
  escalated_at?: string | null;
  escalated_by_user_id?: string | null;
  escalation_note?: string | null;
  awaiting_reply_at?: string | null;
  awaiting_reply_note?: string | null;
  awaiting_reply_by_user_id?: string | null;
  extracted_fields?: ExtractedField[];
  has_international_phone?: boolean;
  is_miscellaneous?: boolean;
  miscellaneous_reason?: string | null;
  addressed_to?: string | null;
  is_icp_referral?: boolean;
  notes?: string | null;
  triage_approved?: boolean;
  triage_approved_by_user_id?: string | null;
  triage_approved_at?: string | null;
  triage_category_final?: number | null;
  triage_category_suggested?: number | null;
  triage_data?: TriageData | null;
  overridden_triage_fields?: string[];  // AXIS165: Field names that have been manually edited
  referral_comments?: ReferralComment[];  // AXIS166: Clinical staff comments on referrals
  ingestion_warnings?: string[];  // AXISREF244: Online form attachment fetch failures at intake
  assigned_to_admin?: boolean;  // AXIS209: Referral assigned to admin flag
  assigned_to_admin_at?: string | null;  // AXIS209: Timestamp of admin assignment
  assigned_to_admin_by_user_id?: string | null;  // AXIS209: User who assigned to admin
  // AXIS076: AE Validation fields
  acc_employer_name?: string | null;
  acc_info_received?: boolean;
  ae_queue_entered_at?: string | null;
  ae_billing_completed?: boolean;
  days_in_ae_queue?: number | null;
  // SR-specific (detail modal)
  sr_referral_number?: string;
  sr_webhook_referral_type?: string | null;
  sr_status?: string | null;
  is_urgent?: boolean | null;
  sr_outbound_pending?: boolean;
  pdf_extraction_pending?: boolean;
  extraction_failed_at?: string | null;
  triage_failed_at?: string | null;
  sr_cancelled_at?: string | null;
  was_completed_at_cancel?: boolean;
  indici_cancel_acknowledged_at?: string | null;
  latest_addendum_at?: string | null;
  latest_addendum_attachment_id?: string | null;
  sr_change_events?: Array<{
    event_type: string;
    created_at: string | null;
    was_completed_at_event: boolean;
  }>;
  // AXISREF390: Location extraction + clinic recommendation fields
  patient_suburb?: string | null;
  patient_city?: string | null;
  patient_location_source?: 'patient_address' | 'practice_address' | null;
  patient_location_confidence?: 'high' | 'medium' | 'low' | null;
  practice_suburb?: string | null;
  practice_city?: string | null;
  referral_latitude?: number | null;
  referral_longitude?: number | null;
  referral_geocoding_match_type?: 'exact' | 'disambiguated' | 'none' | null;
  recommended_clinic_id?: number | null;
  recommended_clinic_name?: string | null;
  clinic_recommendation_source?: string | null;
  clinic_recommendation_confidence?: number | null;
  clinic_recommendation_distance_km?: number | null;
  clinic_recommendation_overridden?: boolean | null;
  clinic_recommendation_override_reason?: string | null;
  // AXISREF392: Referrer type extraction fields
  referrer_type?: string | null;
  referrer_type_id?: string | null;
  referrer_type_status?: string | null;
  referrer_type_match_method?: string | null;
  referrer_type_confidence?: number | null;
  referrer_type_overridden?: boolean | null;
  referrer_type_override_reason?: string | null;
  referrer_type_display_name?: string | null;
}

export interface SrNotificationItem {
  id: string;
  referral_id: string;
  sr_referral_number?: string | null;
  patient_name: string;
  event_type: string;
  event_at: string | null;
  label: string;
  requires_action?: boolean;
}

export interface SrNotificationsFeed {
  action_required: SrNotificationItem[];
  other: SrNotificationItem[];
  action_required_count: number;
}

export interface ReferralComment {
  body: string;
  createdAt: string | null;
  authorName: string;
}

export interface ACCDiagnosis {
  diagnosisCode?: string;
  diagnosisCodeType?: string;
  diagnosisDescription?: string;
  diagnosisSide?: string;
  injuryStatus?: string;
  snomedCode?: string;
  snomedCodeDescription?: string;
}

export interface ACCValidationMetadata {
  acc45Number?: string;
  claimNumber?: string;
  coverStatus?: string;
  partialNHINumber?: string;
  accidentDate?: string;
  diagnoses?: ACCDiagnosis[];
  apiResponse?: Record<string, unknown>;
}

export interface TriageData {
  id: string;
  referral_id: string;
  organisation_id: string | null;
  triage_category_suggested: number | null;
  theme: string | null;
  provisional_diagnosis: string | null;
  onset_type: string | null;
  symptom_duration_bucket: string | null;
  symptom_duration_months: number | null;
  mechanism: string | null;
  athlete_status: boolean | null;
  athlete_level: string | null;
  sport_type: string | null;
  post_surgical_status: boolean | null;
  primary_symptoms: string[] | null;
  functional_limitations: string[] | null;
  comorbidities: string[] | null;
  imaging_findings: string[] | null;
  imaging_types_completed: string[] | null;
  previous_treatments: string[] | null;
  treatment_response: string | null;
  number_of_failed_treatment_modalities: number | null;
  key_phrases_matched: string[] | null;
  escalation_required: boolean | null;
  escalation_reason: string | null;
  secondary_red_flags_present: boolean | null;
  red_flags_detected: Record<string, boolean> | null;
  complexity: 'acute' | 'simple' | 'complex' | null;
  confidence_percentage: number | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface Attachment {
  id: string;
  filename: string;
  type: string;
  size: number;
  mime_type: string;
  storage_key: string;
  uploadedAt: string | null;
  downloadUrl: string;
  delivery_type?: string;
  received_at?: string | null;
  display_label?: string;
}

export interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  display_name: string;
  role: string;
  status: string;
  created_at?: string;
}

export interface FilterConfig {
  status?: string[];
  assignee?: string;
  triageCategory?: number[]; // AXIS164: Triage category filter (1-4, approved only)
  dateFrom?: string;
  dateTo?: string;
  source?: string;
  hasMissingInfo?: boolean;
  search?: string;
  referralType?: string[];
  funder?: string[];
  bodyPart?: string[];
  providerOrg?: string[];
  imagingFacility?: string[];
  providerName?: string[];
  sortBy?: 'receivedDate' | 'name';
  sortOrder?: 'asc' | 'desc';
  // AXISREF399: Recommended clinic filter (names from clinic enum, same pattern as other filters)
  clinicNames?: string[];
}

export interface SavedFilter {
  id: string;
  user_id: string;
  name: string;
  filter_config: FilterConfig;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

export interface ReferralComment {
  id: string;
  referral_id: string;
  body: string;
  author_user_id: string;
  author_name: string;
  created_at: string;
}

export interface PaginatedReferrals {
  referrals: Referral[];
  pagination: {
    current_page: number;
    page_size: number;
    total_count: number;
    total_pages: number;
    has_next: boolean;
    has_previous: boolean;
  };
}

export interface ReferralsSummary {
  filter_options: {
    referral_types: string[];
    funders: string[];
    body_parts: string[];
    provider_orgs: string[];
    imaging_facilities: string[];
    provider_names: string[];
    clinic_names?: string[]; // AXISREF399: Active clinics from enum table
  };
  tab_counts: {
    ready: number;
    missing: number;
    diagnosing: number;
    escalated: number;
    assigned: number;
    unassigned: number;
    miscellaneous: number;
    completed: number;
    awaiting_reply: number;
  };
}

export interface SimulationRequest {
  files: File[];
  senderName: string;
  senderEmail: string;
  subject: string;
  body: string;
  pipelineMode?: 'standalone' | 'pipeline';
}

export interface SimulationStage {
  name?: string;
  stage?: string;
  status?: string;
  message?: string;
  duration_ms?: number;
}

// AXISREF390: Location/clinic block returned by the simulator
export interface SimulationLocationResult {
  patient_suburb: string | null;
  patient_city: string | null;
  patient_location_status: 'extracted' | 'not_found' | 'extraction_failed' | null;
  patient_location_source: 'patient_address' | 'practice_address' | null;
  patient_location_confidence: 'high' | 'medium' | 'low' | null;
  practice_suburb: string | null;
  practice_city: string | null;
  referral_latitude: number | null;
  referral_longitude: number | null;
  geocoding_match_type: 'exact' | 'disambiguated' | 'none' | null;
  recommended_clinic_id: number | null;
  recommended_clinic_name: string | null;
  clinic_recommendation_source: string | null;
  clinic_recommendation_confidence: number | null;
  clinic_recommendation_distance_km: number | null;
}

export interface SimulationReferrerTypeResult {
  source_text: string | null;
  referrer_type: string | null;
  referrer_type_display_name: string | null;
  referrer_type_status: string | null;
  referrer_type_match_method: string | null;
  referrer_type_confidence: number | null;
  referrer_type_overridden?: boolean | null;
}

// AXISREF390: Template referral metadata
export interface TemplateReferral {
  id: string;
  name: string;
  description: string | null;
  file_size: number | null;
  source_info: string | null;
  sender_name: string | null;
  sender_email: string | null;
  email_subject: string | null;
  email_body: string | null;
  blob_paths: string[];
  created_by: string | null;
  created_at: string | null;
}

// AXISREF391: Data returned by the simulate-context endpoint for pre-filling the simulator
export interface ReferralSimulateContext {
  referral_id: string;
  patient_name: string;
  nhi_number: string | null;
  is_simulation: boolean;
  email_context: {
    sender: string | null;
    subject: string | null;
    body_text: string | null;
  };
  attachments: Array<{
    id: string;
    filename: string;
    size: number | null;
    download_url: string;
  }>;
}

export interface SimulationResponse {
  success?: boolean;
  status?: string;
  message?: string;
  stage?: string;
  referral_id?: string;
  internet_message_id?: string;
  ingest_id?: string;
  processing_time_ms?: number;
  is_simulation?: boolean;
  pipeline_mode?: 'standalone' | 'pipeline';
  simulation_queue_visible?: boolean;
  attachment_count?: number;
  duplicate?: boolean;
  duplicate_referral_id?: string;
  referral?: Partial<Referral> & Record<string, unknown>;
  extracted?: Record<string, unknown>;
  extracted_data?: Record<string, unknown>;
  extracted_fields?: Record<string, unknown>;
  triage?: Record<string, unknown>;
  triage_data?: Record<string, unknown>;
  location?: SimulationLocationResult;
  referrer_type?: SimulationReferrerTypeResult;
  raw?: Record<string, unknown>;
  stages?: SimulationStage[];
  [key: string]: unknown;
}

export class SimulationApiError extends Error {
  status: number;
  stage?: string;
  details?: Record<string, unknown>;

  constructor(message: string, status: number, details?: Record<string, unknown>) {
    super(message);
    this.name = 'SimulationApiError';
    this.status = status;
    this.stage = typeof details?.stage === 'string' ? details.stage : undefined;
    this.details = details;
  }
}

// ---------------------------------------------------------------------------
// SR Queue types (AXISREF274)
// ---------------------------------------------------------------------------

export interface SRReferral {
  id: string;
  sr_referral_number: string;
  patient_given_name: string;
  patient_family_name: string;
  patient_dob: string | null;
  patient_nhi: string | null;
  referring_provider_name: string | null;
  referring_provider_org: string | null;
  status: string;
  triage_approved: boolean;
  triage_category_final: number | null;
  referral_type: string | null;
  funder: string | null;
  urgency: number | null;
  assigned_to_user_id: string | null;
  created_at: string;
  // Onforward fields (AXISREF318)
  is_on_refer: number | null;
  on_refer_info: string | null;
  referred_from_id: number | null;
  is_urgent?: boolean | null;
  sr_cancelled_at?: string | null;
  was_completed_at_cancel?: boolean;
}

/** Map SR queue list row to Referral shape for instant detail modal open. */
export function mapSrReferralToReferral(sr: SRReferral): Referral {
  return {
    id: sr.id,
    source: 'sr',
    is_sr_referral: true,
    sr_referral_number: sr.sr_referral_number,
    is_urgent: sr.is_urgent,
    sr_cancelled_at: sr.sr_cancelled_at,
    was_completed_at_cancel: sr.was_completed_at_cancel,
    patient_first_name: sr.patient_given_name,
    patient_last_name: sr.patient_family_name,
    patient_dob: sr.patient_dob,
    nhi_number: sr.patient_nhi,
    acc_claim_number: null,
    contact_phone: null,
    contact_email: null,
    clinical_notes: null,
    status: sr.status,
    priority: null,
    referral_type: sr.referral_type,
    sub_status: null,
    body_side: null,
    body_part: null,
    date_of_injury: null,
    mechanism_of_injury: null,
    is_acc_related: null,
    acc_number_validated: null,
    declined_acc_approved_at: null,
    declined_acc_approved_by_user_id: null,
    declined_acc_approval_note: null,
    imaging_performed: null,
    imaging_facility: null,
    referring_provider_name: sr.referring_provider_name,
    referring_provider_org: sr.referring_provider_org,
    referring_provider_email: null,
    referring_provider_edi: null,
    funder: sr.funder,
    received_at: sr.created_at,
    created_at: sr.created_at,
    updated_at: null,
    attachment_count: 0,
    assigned_to_user_id: sr.assigned_to_user_id,
    triage_approved: sr.triage_approved,
    triage_category_final: sr.triage_category_final,
    triage_category_suggested: sr.triage_category_final,
  };
}

export interface PaginatedSRReferrals {
  referrals: SRReferral[];
  pagination: {
    current_page: number;
    page_size: number;
    total_count: number;
    total_pages: number;
    has_next: boolean;
    has_previous: boolean;
  };
}

export interface SRConnectionStatus {
  connected: boolean;
  subscription_active: boolean;
  subscribed_at: string | null;
  sr_user_id: string | null;
  connected_by: string | null;
}

export interface SRSettingStatus {
  key: string;
  label: string;
  present: boolean;
  /** AXISREF441: oauth | jwt | webhook | extraction — drives card grouping. */
  group: 'oauth' | 'jwt' | 'webhook' | 'extraction';
}

export interface SRConfigStatus {
  configured: boolean;
  checks: Record<string, boolean>;
  settings: SRSettingStatus[];
  missing_for_oauth: string[];
  missing_for_webhooks: string[];
  missing_for_extraction?: string[];
  extraction_configured?: boolean;
}

export interface SRQueueSummary {
  tab_counts: {
    ai_triaging: number;
    new: number;
    assigned: number;
    awaiting_reply: number;
    ready_for_indici: number;
  };
  connection: SRConnectionStatus;
  failed_ingest_count?: number;
}

export interface SRIngestFailure {
  failure_type: string;
  post_id: string | null;
  display_referral_no: string | null;
  sr_referral_id: string | null;
  sr_id: number | null;
  referral_type: string | null;
  patient_given_name: string | null;
  patient_family_name: string | null;
  patient_nhi: string | null;
  gp_name: string | null;
  gp_practice: string | null;
  error_message: string | null;
  processing_status: string | null;
  occurred_at: string | null;
}

export interface SRIngestFailuresResponse {
  failures: SRIngestFailure[];
  pagination: {
    total_count: number;
    current_page: number;
    page_size: number;
    total_pages: number;
    has_next: boolean;
    has_previous: boolean;
  };
  summary: {
    total_open_failures: number;
  };
}

/** AXISREF491: ACC validation health per intake path (admin only). */
export interface ValidationHealthPath {
  healthy: boolean;
  last_success_at: string | null;
  hours_since_success: number | null;
  total_7d: number;
  by_status_7d: Record<string, number>;
  last_validated_at: string | null;
}

export interface ValidationHealthResponse {
  health_window_hours: number;
  lookback_days: number;
  email: ValidationHealthPath;
  sr: ValidationHealthPath;
}

export class ApiService {
  private static baseUrl = (import.meta.env.VITE_FUNCTION_APP_URL || 'https://func-axis-referral-dev.azurewebsites.net') + '/api';

  /**
   * Wrapper for fetch that adds Entra ID access token via MSAL to Authorization header
   */
  private static async fetchWithAuth(
    url: string,
    options: RequestInit = {}
  ): Promise<Response> {
    // Acquire Entra ID access token via MSAL
    let token: string | null = null;
    try {
      const tokenResult = await acquireTokenSilent();
      token = tokenResult.accessToken;
    } catch (tokenError) {
      // Token acquisition failed - user needs to re-authenticate
      console.warn('Failed to acquire access token:', tokenError);
    }

    // Merge headers, adding Authorization with Entra ID token
    const headers: HeadersInit = {
      ...(options.headers as HeadersInit || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    const response = await fetch(url, {
      ...options,
      headers,
    });

    // Intercept 401 responses (unauthorized/expired token)
    if (response.status === 401) {
      // Log for debugging but don't auto-logout - let the component handle it
      console.warn('API returned 401 - token may be expired or invalid');
      // Don't clear AuthService or redirect automatically - this is too aggressive
      // and can cause logout loops in production. Let the route guards handle it.
    }

    return response;
  }

  /**
   * Run a PDF through the referral intake pipeline without requiring an email.
   * Content-Type is intentionally omitted so the browser supplies the multipart boundary.
   */
  static async simulateReferral(request: SimulationRequest): Promise<SimulationResponse> {
    const formData = new FormData();
    for (const f of request.files) {
      formData.append('files', f);
    }
    formData.append('sender_name', request.senderName);
    formData.append('sender_email', request.senderEmail);
    formData.append('subject', request.subject);
    formData.append('body_text', request.body);
    if (request.pipelineMode) {
      formData.append('pipeline_mode', request.pipelineMode);
    }

    const response = await this.fetchWithAuth(`${this.baseUrl}/referrals/simulate`, {
      method: 'POST',
      body: formData,
    });
    const payload = await response.json().catch(() => ({})) as Record<string, unknown>;

    if (!response.ok) {
      const message =
        (typeof payload.error === 'string' && payload.error) ||
        (typeof payload.message === 'string' && payload.message) ||
        `Simulation failed (${response.status})`;
      throw new SimulationApiError(message, response.status, payload);
    }

    return payload as SimulationResponse;
  }

  /**
   * Get all active template referrals (admin-only). AXISREF390.
   */
  static async getTemplateReferrals(): Promise<TemplateReferral[]> {
    const response = await this.fetchWithAuth(`${this.baseUrl}/template-referrals`, {
      method: 'GET',
    });
    if (!response.ok) throw new Error(`Failed to fetch templates (${response.status})`);
    const data = await response.json();
    return (data.templates || []) as TemplateReferral[];
  }

  /**
   * Download a template referral PDF blob. AXISREF390.
   */
  static async downloadTemplateReferral(templateId: string): Promise<Blob> {
    const response = await this.fetchWithAuth(
      `${this.baseUrl}/template-referrals/${encodeURIComponent(templateId)}/download`,
      { method: 'GET' }
    );
    if (!response.ok) throw new Error(`Failed to download template (${response.status})`);
    return response.blob();
  }

  /**
   * Upload one or more template referral PDFs. AXISREF390/391.
   */
  static async createTemplateReferral(
    files: File | File[],
    name: string,
    description?: string,
    sourceInfo?: string,
    senderName?: string,
    senderEmail?: string,
    emailSubject?: string,
    emailBody?: string,
  ): Promise<TemplateReferral> {
    const formData = new FormData();
    const fileList = Array.isArray(files) ? files : [files];
    for (const f of fileList) {
      formData.append('files', f);
    }
    formData.append('name', name);
    if (description) formData.append('description', description);
    if (sourceInfo) formData.append('source_info', sourceInfo);
    if (senderName) formData.append('sender_name', senderName);
    if (senderEmail) formData.append('sender_email', senderEmail);
    if (emailSubject) formData.append('email_subject', emailSubject);
    if (emailBody) formData.append('email_body', emailBody);

    const response = await this.fetchWithAuth(`${this.baseUrl}/template-referrals/create`, {
      method: 'POST',
      body: formData,
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({})) as Record<string, unknown>;
      const msg = (typeof body.error === 'string' && body.error) || `HTTP ${response.status}`;
      throw new Error(msg);
    }
    return (await response.json()) as TemplateReferral;
  }

  /**
   * Fetch email context and attachment list for re-simulating an existing referral. AXISREF391.
   */
  static async getReferralSimulateContext(referralId: string): Promise<ReferralSimulateContext> {
    const response = await this.fetchWithAuth(
      `${this.baseUrl}/referrals/${encodeURIComponent(referralId)}/simulate-context`,
      { method: 'GET' }
    );
    if (!response.ok) {
      const body = await response.json().catch(() => ({})) as Record<string, unknown>;
      const msg = (typeof body.error === 'string' && body.error) || `HTTP ${response.status}`;
      throw new Error(msg);
    }
    return (await response.json()) as ReferralSimulateContext;
  }

  /**
   * Create a template from an existing referral (copies blob + email context). AXISREF391.
   */
  static async createTemplateFromReferral(
    referralId: string,
    name: string,
    description?: string,
  ): Promise<TemplateReferral> {
    const response = await this.fetchWithAuth(`${this.baseUrl}/template-referrals/from-referral`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ referral_id: referralId, name, description }),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({})) as Record<string, unknown>;
      const msg = (typeof body.error === 'string' && body.error) || `HTTP ${response.status}`;
      throw new Error(msg);
    }
    return (await response.json()) as TemplateReferral;
  }

  /**
   * Get referrals summary with filter options and tab counts
   */
  static async getReferralsSummary(): Promise<ReferralsSummary> {
    try {
      const response = await this.fetchWithAuth(`${this.baseUrl}/referrals/summary`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching referrals summary:', error);
      throw new Error('Failed to fetch referrals summary');
    }
  }

  /**
   * Get referrals with pagination and filtering
   */
  static async getReferrals(
    page: number = 1,
    pageSize: number = 10,
    status?: string,
    search?: string,
    assigneeId?: string,
    triageTab?: 'triaged' | 'approved' | 'assigned_to_admin',  // AXIS209: Added assigned_to_admin
    triageCategory?: number[], // AXIS164: Filter by approved triage category (1-4)
    aeQueue?: boolean, // AXIS235: Filter for AE validation queue
    signal?: AbortSignal, // Allow cancellation of in-flight requests
    // AXIS242: Additional server-side filter parameters
    referralType?: string[],
    funder?: string[],
    bodyPart?: string[],
    providerOrg?: string[],
    imagingFacility?: string[],
    providerName?: string[],
    searchScope?: 'reception',
    searchFields?: 'name_nhi',
    // AXISREF399: Recommended clinic filter (enum-driven, same pattern as other string filters)
    clinicNames?: string[],
    // AXISREF352: Server-side sort
    sort?: 'received_at' | 'patient_name',
    sortDirection?: 'asc' | 'desc',
  ): Promise<PaginatedReferrals> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
      });

      if (status) {
        params.append('status', status);
      }

      if (search) {
        params.append('search', search);
      }

      if (assigneeId) {
        params.append('assignee', assigneeId);
      }

      if (triageCategory && triageCategory.length > 0) {
        params.append('triage_category', triageCategory.join(','));
      }

      if (triageTab) {
        params.append('triage_tab', triageTab);
      }

      if (aeQueue) {
        params.append('ae_queue', 'true'); // AXIS235
      }

      // AXIS242: Add new server-side filter parameters
      if (referralType && referralType.length > 0) {
        params.append('referral_type', referralType.join(','));
      }

      if (funder && funder.length > 0) {
        params.append('funder', funder.join(','));
      }

      if (bodyPart && bodyPart.length > 0) {
        params.append('body_part', bodyPart.join(','));
      }

      if (providerOrg && providerOrg.length > 0) {
        params.append('provider_org', providerOrg.join(','));
      }

      if (imagingFacility && imagingFacility.length > 0) {
        params.append('imaging_facility', imagingFacility.join(','));
      }

      if (providerName && providerName.length > 0) {
        params.append('provider_name', providerName.join(','));
      }

      if (searchScope) {
        params.append('search_scope', searchScope);
      }

      if (searchFields) {
        params.append('search_fields', searchFields);
      }

      // AXISREF399: Recommended clinic filter (enum-driven string names, same as other filters)
      if (clinicNames && clinicNames.length > 0) {
        params.append('clinic_name', clinicNames.join(','));
      }

      if (sort) {
        params.append('sort', sort);
      }

      if (sortDirection) {
        params.append('sort_direction', sortDirection);
      }

      const response = await this.fetchWithAuth(`${this.baseUrl}/get-referrals?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      // Don't log or wrap AbortErrors - they're expected when requests are cancelled
      if (error instanceof Error && error.name === 'AbortError') {
        throw error;
      }
      console.error('Error fetching referrals:', error);
      throw new Error('Failed to fetch referrals');
    }
  }

  /**
   * Get a specific referral by ID with attachments
   */
  static async getReferralById(referralId: string): Promise<Referral> {
    try {
      const response = await this.fetchWithAuth(`${this.baseUrl}/referral/${referralId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Referral not found');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching referral:', error);
      throw new Error('Failed to fetch referral');
    }
  }

  private static async saveResponseAsDownload(
    response: Response,
    filename: string,
  ): Promise<void> {
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  /**
   * Download an email referral attachment
   */
  static async downloadAttachment(attachmentId: string, filename: string): Promise<void> {
    try {
      const response = await this.fetchWithAuth(
        `${this.baseUrl}/download-attachment/${attachmentId}`,
        { method: 'GET' },
      );
      await this.saveResponseAsDownload(response, filename);
    } catch (error) {
      console.error('Error downloading attachment:', error);
      throw new Error('Failed to download attachment');
    }
  }

  /**
   * Fetch a referral attachment as a Blob (for pre-filling the simulator). AXISREF391.
   */
  static async fetchAttachmentAsBlob(attachmentId: string): Promise<Blob> {
    const response = await this.fetchWithAuth(
      `${this.baseUrl}/download-attachment/${attachmentId}`,
      { method: 'GET' },
    );
    if (!response.ok) throw new Error(`Failed to fetch attachment (${response.status})`);
    return response.blob();
  }

  /**
   * Download an SR referral attachment from blob storage
   */
  static async downloadSrAttachment(
    srReferralId: string,
    attachmentId: string,
    filename: string,
  ): Promise<void> {
    try {
      const response = await this.fetchWithAuth(
        `${this.baseUrl}/sr/referrals/${encodeURIComponent(srReferralId)}/attachments/${encodeURIComponent(attachmentId)}`,
        { method: 'GET' },
      );
      await this.saveResponseAsDownload(response, filename);
    } catch (error) {
      console.error('Error downloading SR attachment:', error);
      throw new Error('Failed to download attachment');
    }
  }

  /**
   * Get download URL for an attachment (for preview or direct linking)
   */
  static getAttachmentDownloadUrl(attachmentId: string): string {
    return `${this.baseUrl}/download-attachment/${attachmentId}`;
  }

  /** Approve a currently declined ACC claim with a mandatory staff note. */
  static async approveDeclinedAcc(referralId: string, note: string): Promise<{
    success: boolean;
    referral_id: string;
    source_type: 'email' | 'sr';
  }> {
    const response = await this.fetchWithAuth(
      `${this.baseUrl}/referrals/${encodeURIComponent(referralId)}/approve-declined-acc`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note }),
      },
    );
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const error = new Error(
        (typeof data.error === 'string' && data.error) || `HTTP error! status: ${response.status}`,
      ) as Error & { error_code?: string };
      error.error_code = data.error_code;
      throw error;
    }
    return data;
  }

  /**
   * Update referral status
   */
  static async updateReferralStatus(
    referralId: string,
    status: string,
    options?: { srResponse?: SRCompleteResponse; miscNoIndiciConfirm?: boolean },
  ): Promise<UpdateReferralStatusResult> {
    try {
      const payload: Record<string, unknown> = { status };
      if (options?.srResponse) {
        payload.sr_response = options.srResponse;
      }
      // AXISREF437: explicit client confirmation for the no-Indici miscellaneous completion
      if (options?.miscNoIndiciConfirm) {
        payload.misc_no_indici_confirm = true;
      }

      const response = await this.fetchWithAuth(`${this.baseUrl}/referrals/${referralId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage =
          (typeof errorData.error === 'string' && errorData.error) ||
          (typeof errorData.message === 'string' && errorData.message) ||
          (typeof errorData.detail === 'string' && errorData.detail) ||
          (typeof errorData.reason === 'string' && errorData.reason) ||
          `HTTP error! status: ${response.status}`;
        const error = new Error(errorMessage) as Error & Partial<UpdateReferralStatusResult>;
        Object.assign(error, {
          error_code: errorData.error_code,
          missing_fields: errorData.missing_fields,
          missing_field_count: errorData.missing_field_count,
          blocker_code: errorData.blocker_code,
          required_action: errorData.required_action,
        });
        throw error;
      }

      const data = await response.json();
      return {
        referral: data.referral,
        sr_message: data.sr_message,
        success: data.success,
        status: data.status,
        error: data.error,
        message: data.message,
        error_code: data.error_code,
        missing_fields: data.missing_fields,
        missing_field_count: data.missing_field_count,
        blocker_code: data.blocker_code,
        required_action: data.required_action,
      };
    } catch (error) {
      console.error('Error updating referral status:', error);
      // Preserve the structured blocker contract for the modal.
      if (error && typeof error === 'object') {
        const source = error as Partial<UpdateReferralStatusResult>;
        const err = new Error(error instanceof Error ? error.message : 'Failed to update referral status') as Error & Partial<UpdateReferralStatusResult>;
        Object.assign(err, {
          error_code: source.error_code,
          missing_fields: source.missing_fields,
          missing_field_count: source.missing_field_count,
          blocker_code: source.blocker_code,
          required_action: source.required_action,
        });
        throw err;
      }
      throw new Error(error instanceof Error ? error.message : 'Failed to update referral status');
    }
  }

  /**
   * Update AE (ACC Accredited Employer) validation status - AXIS076
   */
  static async updateAeStatus(
    referralId: string,
    data: {
      acc_employer_name?: string;
      acc_info_received?: boolean;
      enter_ae_queue?: boolean;
      exit_ae_queue?: boolean;
      ae_billing_completed?: boolean;
    }
  ): Promise<{ success: boolean; ae_status: any }> {
    try {
      const response = await this.fetchWithAuth(`${this.baseUrl}/referrals/${referralId}/ae-status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating AE status:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to update AE status');
    }
  }

  /**
   * Update referral data
   * AXISREF439: whitelist of modal-editable detail fields actually accepted
   * by PATCH /api/referrals/{id} for both standard and SR referrals. Keeps
   * display/workflow-only keys (status, attachments, extracted_fields, sr_*)
   * out of the PATCH body so the backend contract check never rejects saves.
   */
  static readonly EDITABLE_REFERRAL_FIELDS = [
    'patient_first_name',
    'patient_last_name',
    'patient_dob',
    'nhi_number',
    'acc_claim_number',
    'contact_phone',
    'contact_email',
    'clinical_notes',
    'referral_type',
    'funder',
    'acc_employer_name',
    'is_acc_related',
    'acc_number_validated',
    'body_side',
    'body_part',
    'date_of_injury',
    'mechanism_of_injury',
    'referring_provider_name',
    'referring_provider_org',
    'referring_provider_email',
    'referring_provider_edi',
    'priority',
    'triage_category_final',
  ] as const;

  static async updateReferral(referralId: string, updateData: Partial<Referral>): Promise<Referral> {
    try {
      const filtered: Record<string, any> = {};
      (Object.keys(updateData) as Array<keyof Referral>).forEach(key => {
        if ((ApiService.EDITABLE_REFERRAL_FIELDS as readonly string[]).includes(key as string)) {
          filtered[key] = updateData[key];
        }
      });
      const response = await this.fetchWithAuth(`${this.baseUrl}/referrals/${referralId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(filtered),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.referral || data;
    } catch (error) {
      console.error('Error updating referral:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to update referral');
    }
  }

  /**
   * Assign referral to a user with JWT authorization
   */
  static async assignReferral(
    referralId: string,
    assignedToUserId: string | null,
    _authToken: string, // Kept for backward compatibility, token is retrieved from localStorage
    priority?: string,
    dueDate?: string,
    notes?: string
  ): Promise<Referral> {
    try {
      const body: any = {
        assigned_to_user_id: assignedToUserId,
      };

      if (priority) {
        body.priority = priority;
      }
      if (dueDate) {
        body.due_date = dueDate;
      }
      if (notes) {
        body.notes = notes;
      }

      const response = await this.fetchWithAuth(`${this.baseUrl}/referrals/${referralId}/assignment`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.referral || data;
    } catch (error) {
      console.error('Error assigning referral:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to assign referral');
    }
  }

  /**
   * Escalate a referral
   */
  static async escalateReferral(
    referralId: string,
    escalationNote: string,
    _authToken: string // Kept for backward compatibility, token is retrieved from localStorage
  ): Promise<Referral> {
    try {
      const response = await this.fetchWithAuth(`${this.baseUrl}/referrals/${referralId}/escalate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          escalation_note: escalationNote,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.referral;
    } catch (error) {
      console.error('Error escalating referral:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to escalate referral');
    }
  }

  /**
   * Resolve an escalation for a referral
   */
  static async resolveEscalation(referralId: string, _authToken: string): Promise<Referral> {
    // _authToken kept for backward compatibility, token is retrieved from localStorage
    try {
      const response = await this.fetchWithAuth(`${this.baseUrl}/referrals/${referralId}/resolve-escalation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.referral;
    } catch (error) {
      console.error('Error resolving escalation:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to resolve escalation');
    }
  }

  /**
   * Assign a referral to admin for missing information completion (AXIS209)
   */
  static async assignToAdmin(referralId: string): Promise<Referral> {
    try {
      const response = await this.fetchWithAuth(`${this.baseUrl}/referrals/${referralId}/assign-to-admin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.referral;
    } catch (error) {
      console.error('Error assigning referral to admin:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to assign referral to admin');
    }
  }

  /**
   * Mark a referral as awaiting reply
   */
  static async markAwaitingReply(
    referralId: string,
    note: string,
    _authToken: string // Kept for backward compatibility, token is retrieved from localStorage
  ): Promise<Referral> {
    try {
      const response = await this.fetchWithAuth(`${this.baseUrl}/referrals/${referralId}/mark-awaiting-reply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          note: note,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.referral;
    } catch (error) {
      console.error('Error marking referral as awaiting reply:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to mark referral as awaiting reply');
    }
  }

  /**
   * Update triage category and/or triage data fields for a referral
   */
  static async updateTriageCategory(
    referralId: string, 
    triageCategoryFinal?: number,
    triageDataUpdates?: Partial<TriageData>
  ): Promise<Referral> {
    try {
      const body: any = {};
      
      if (triageCategoryFinal !== undefined) {
        body.triage_category_final = triageCategoryFinal;
      }
      
      if (triageDataUpdates) {
        Object.assign(body, triageDataUpdates);
      }
      
      const url = `${this.baseUrl}/referrals/${referralId}/triage-category`;
      console.log('Updating triage category:', { url, referralId, body });
      
      const response = await this.fetchWithAuth(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        const errorMessage = error.error || `HTTP error! status: ${response.status}`;
        
        console.error('Triage update failed:', {
          status: response.status,
          statusText: response.statusText,
          error,
          url,
          headers: Object.fromEntries(response.headers.entries())
        });
        
        throw new Error(errorMessage);
      }

      const data = await response.json();
      return data.referral;
    } catch (error) {
      console.error('Error updating triage data:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to update triage data');
    }
  }

  /**
   * Approve triage for a referral
   */
  static async approveTriage(referralId: string): Promise<void> {
    try {
      const response = await this.fetchWithAuth(`${this.baseUrl}/referrals/${referralId}/approve-triage`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `HTTP error! status: ${response.status}`);
      }

      // Backend returns success, but we'll fetch the full referral separately
      await response.json();
    } catch (error) {
      console.error('Error approving triage:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to approve triage');
    }
  }

  /**
   * Extract triage data for a completed referral
   */
  static async extractTriage(referralId: string): Promise<{ success: boolean; message: string; triage_data?: any; attachment_ocr_warnings?: string[] }> {
    try {
      const response = await this.fetchWithAuth(`${this.baseUrl}/referrals/${referralId}/extract-triage`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        const errorMessage = error.error || `HTTP error! status: ${response.status}`;
        throw new Error(errorMessage);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error extracting triage:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to extract triage');
    }
  }

  /**
   * AXIS212: Upload an attachment to a referral
   * Uses FormData for multipart upload (not JSON)
   */
  static async uploadAttachment(
    referralId: string,
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<{ success: boolean; filename: string; file_size: number; content_type: string; blob_path: string; attachment_id: string; uploaded_at: string }> {
    try {
      // Acquire Entra ID access token via MSAL
      let token: string | null = null;
      try {
        const tokenResult = await acquireTokenSilent();
        token = tokenResult.accessToken;
      } catch (tokenError) {
        console.warn('Failed to acquire access token:', tokenError);
      }

      // Create FormData for multipart upload
      const formData = new FormData();
      formData.append('file', file);

      const url = `${this.baseUrl}/referrals/${referralId}/attachments`;

      // Use XMLHttpRequest for progress tracking
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        if (onProgress) {
          xhr.upload.addEventListener('progress', (event) => {
            if (event.lengthComputable) {
              const progress = Math.round((event.loaded / event.total) * 100);
              onProgress(progress);
            }
          });
        }

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const data = JSON.parse(xhr.responseText);
              resolve(data);
            } catch (e) {
              reject(new Error('Invalid response format'));
            }
          } else {
            try {
              const error = JSON.parse(xhr.responseText);
              reject(new Error(error.error || `HTTP error! status: ${xhr.status}`));
            } catch {
              reject(new Error(`HTTP error! status: ${xhr.status}`));
            }
          }
        });

        xhr.addEventListener('error', () => {
          reject(new Error('Network error occurred'));
        });

        xhr.addEventListener('abort', () => {
          reject(new Error('Upload aborted'));
        });

        xhr.open('POST', url);
        if (token) {
          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        }
        // Note: Do NOT set Content-Type header - browser will set it with boundary for FormData
        xhr.send(formData);
      });
    } catch (error) {
      console.error('Error uploading attachment:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to upload attachment');
    }
  }

  /**
   * AXIS212: Delete an attachment from a referral
   */
  static async deleteAttachment(
    referralId: string,
    attachmentId: string
  ): Promise<{ success: boolean; attachment_id: string; filename: string; message: string }> {
    try {
      const response = await this.fetchWithAuth(
        `${this.baseUrl}/referrals/${referralId}/attachments/${attachmentId}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error deleting attachment:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to delete attachment');
    }
  }

  /**
   * Get all saved filters for the current user
   */
  static async getSavedFilters(): Promise<SavedFilter[]> {
    try {
      const response = await this.fetchWithAuth(`${this.baseUrl}/user/saved-filters`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching saved filters:', error);
      throw new Error('Failed to fetch saved filters');
    }
  }

  /**
   * Create a new saved filter
   */
  static async createSavedFilter(
    name: string,
    filterConfig: FilterConfig,
    isDefault: boolean = false
  ): Promise<SavedFilter> {
    try {
      const response = await this.fetchWithAuth(`${this.baseUrl}/user/saved-filters`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name,
          filter_config: filterConfig,
          is_default: isDefault,
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error creating saved filter:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to create saved filter');
    }
  }

  /**
   * Update an existing saved filter
   */
  static async updateSavedFilter(
    filterId: string,
    name?: string,
    filterConfig?: FilterConfig
  ): Promise<SavedFilter> {
    try {
      const body: any = {};
      if (name !== undefined) body.name = name;
      if (filterConfig !== undefined) body.filter_config = filterConfig;

      const response = await this.fetchWithAuth(`${this.baseUrl}/user/saved-filters/${filterId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error updating saved filter:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to update saved filter');
    }
  }

  /**
   * Delete a saved filter
   */
  static async deleteSavedFilter(filterId: string): Promise<void> {
    try {
      const response = await this.fetchWithAuth(`${this.baseUrl}/user/saved-filters/${filterId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error deleting saved filter:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to delete saved filter');
    }
  }

  /**
   * Delete a referral (Admin only)
   * AXIS145: Add delete option to referral queue actions
   */
  static async deleteReferral(referralId: string): Promise<void> {
    try {
      const response = await this.fetchWithAuth(`${this.baseUrl}/referrals/${referralId}/delete`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error deleting referral:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to delete referral');
    }
  }

  /**
   * Bulk delete referrals (Admin only)
   * AXIS145: Add delete option to referral queue actions
   */
  static async bulkDeleteReferrals(referralIds: string[]): Promise<{ deleted_count: number; failed_count: number; failed?: Array<{ referral_id: string; reason: string }> }> {
    try {
      const response = await this.fetchWithAuth(`${this.baseUrl}/referrals/bulk-delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ referral_ids: referralIds }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error bulk deleting referrals:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to bulk delete referrals');
    }
  }

  /**
   * Set a saved filter as the default
   */
  static async setDefaultFilter(filterId: string): Promise<SavedFilter> {
    try {
      const response = await this.fetchWithAuth(`${this.baseUrl}/user/saved-filters/${filterId}/set-default`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error setting default filter:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to set default filter');
    }
  }

  /**
   * Trigger validation for identifiers on a referral
   */
  static async triggerValidation(referralId: string, force: boolean = true): Promise<{ referral_id: string; nhi_validation?: any; acc_validation?: any }> {
    try {
      const response = await this.fetchWithAuth(`${this.baseUrl}/referrals/${referralId}/validate-identifiers?force=${force}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error triggering validation:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to trigger validation');
    }
  }

  // ---------------------------------------------------------------------------
  // SR Queue (AXISREF274)
  // ---------------------------------------------------------------------------

  /**
   * Get paginated SR referrals from the sr_referrals table.
   *
   * Tab mapping:
   *   AI Triaging  → triageApproved=false
   *   New          → status='new'           (backend enforces triage_approved=TRUE + unassigned)
   *   Assigned     → status='assigned'      (backend maps to assigned_to_user_id IS NOT NULL)
   *   Awaiting Reply → status='awaiting_reply'
   *   Ready for Indici → status='ready_for_indici'
   */
  static async getSRReferrals(
    page: number = 1,
    pageSize: number = 25,
    status?: string,
    triageApproved?: boolean,
  ): Promise<PaginatedSRReferrals> {
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
    });
    if (status) params.append('status', status);
    if (triageApproved !== undefined) params.append('triage_approved', String(triageApproved));

    return srFetch(
      'referrals',
      () =>
        this.fetchWithAuth(`${this.baseUrl}/sr/referrals?${params}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        }),
      (response) => response.json(),
    );
  }

  /**
   * Get SR Queue tab counts and connection status.
   */
  static async getSRReferralsSummary(): Promise<SRQueueSummary> {
    return srFetch(
      'summary',
      () =>
        this.fetchWithAuth(`${this.baseUrl}/sr/referrals/summary`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        }),
      (response) => response.json(),
    );
  }

  static async getSRIngestFailures(
    page: number = 1,
    pageSize: number = 25,
  ): Promise<SRIngestFailuresResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
    });
    return srFetch(
      'ingest_failures',
      () =>
        this.fetchWithAuth(`${this.baseUrl}/sr/ingest/failures?${params}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        }),
      (response) => response.json(),
    );
  }

  /** Replay a failed SR webhook ingest (admin only). */
  static async retrySRIngest(postId: string): Promise<{
    success: boolean;
    post_id: string;
    status?: string;
    result?: Record<string, unknown>;
  }> {
    return srFetch(
      'ingest_retry',
      () =>
        this.fetchWithAuth(
          `${this.baseUrl}/sr/ingest/failures/${encodeURIComponent(postId)}/retry`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
          },
        ),
      (response) => response.json(),
    );
  }

  /** AXISREF491: ACC validation health per intake path (admin only). */
  static async getValidationHealth(): Promise<ValidationHealthResponse> {
    return srFetch(
      'validation_health',
      () =>
        this.fetchWithAuth(`${this.baseUrl}/validation/health`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        }),
      (response) => response.json(),
    );
  }

  /**
   * Queue a triage-failed SR referral for manual triage (admin/manager).
   */
  static async queueSrManualTriage(srReferralId: string): Promise<{
    success: boolean;
    already_queued?: boolean;
    sr_referral_id: string;
    triage_data_id?: string;
  }> {
    return srFetch(
      'queue_manual_triage',
      () =>
        this.fetchWithAuth(
          `${this.baseUrl}/sr/referrals/${srReferralId}/queue-manual-triage`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
          },
        ),
      (response) => response.json(),
    );
  }

  /**
   * Get SR connection status (admin only).
   */
  static async getSRConnection(): Promise<SRConnectionStatus> {
    return srFetch(
      'connection',
      () =>
        this.fetchWithAuth(`${this.baseUrl}/sr/connection`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        }),
      (response) => response.json(),
    );
  }

  /**
   * Get SR server configuration presence (admin only — booleans, no secret values).
   */
  static async getSRConfigStatus(): Promise<SRConfigStatus> {
    return srFetch(
      'config_status',
      () =>
        this.fetchWithAuth(`${this.baseUrl}/sr/config-status`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        }),
      (response) => response.json(),
    );
  }

  /**
   * Start SR OAuth flow — returns authorization URL for redirect.
   */
  static async startSROAuth(): Promise<{ auth_url: string }> {
    return srFetch(
      'oauth_start',
      () =>
        this.fetchWithAuth(`${this.baseUrl}/sr/oauth/start`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        }),
      (response) => response.json(),
    );
  }

  /**
   * Disconnect SR integration (admin only).
   */
  static async disconnectSR(): Promise<{ success: boolean }> {
    return srFetch(
      'disconnect',
      () =>
        this.fetchWithAuth(`${this.baseUrl}/sr/connection/disconnect`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        }),
      (response) => response.json(),
    );
  }

  /**
   * Send manual GP message for an SR referral via API 4.3.
   */
  static async sendSRReferralMessage(referralId: string, body: string): Promise<{ success: boolean; message_id?: string }> {
    return srFetch(
      'message',
      () =>
        this.fetchWithAuth(`${this.baseUrl}/sr/referrals/${referralId}/message`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ body }),
        }),
      (response) => response.json(),
    );
  }

  /**
   * Load a missing-fields clarification draft for an SR referral (API 4.3 type 3).
   */
  static async getSRClarificationDraft(referralId: string): Promise<{ body: string }> {
    return srFetch(
      'message',
      () =>
        this.fetchWithAuth(
          `${this.baseUrl}/sr/referrals/${referralId}/message?draft=missing_fields`,
          {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
          },
        ),
      (response) => response.json(),
    );
  }

  /**
   * Send clarification to GP via SR API 4.3 and mark referral awaiting_reply on success.
   */
  static async requestSRClarification(
    referralId: string,
    body: string,
    options?: { awaitingReplyNote?: string },
  ): Promise<{ success: boolean; status?: string; message_id?: string }> {
    return srFetch(
      'message',
      () =>
        this.fetchWithAuth(`${this.baseUrl}/sr/referrals/${referralId}/message`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            body,
            mark_awaiting_reply: true,
            awaiting_reply_note: options?.awaitingReplyNote,
          }),
        }),
      (response) => response.json(),
    );
  }

  /**
   * Get fresh SSO link to SR Referral Hub (admin only).
   */
  static async getSRHubLoginLink(): Promise<{ url: string }> {
    return srFetch(
      'hub_login',
      () =>
        this.fetchWithAuth(`${this.baseUrl}/sr/hub-login`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        }),
      (response) => response.json(),
    );
  }

  /**
   * Retry failed post-ingest SR outbound notification (admin only).
   */
  static async retrySROutbound(referralId: string): Promise<{ success: boolean }> {
    return srFetch(
      'retry_outbound',
      () =>
        this.fetchWithAuth(`${this.baseUrl}/sr/referrals/${referralId}/retry-outbound`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        }),
      (response) => response.json(),
    );
  }

  /** SR notification bell feed (queue roles). */
  static async getSrNotifications(): Promise<SrNotificationsFeed> {
    return srFetch(
      'notifications',
      () =>
        this.fetchWithAuth(`${this.baseUrl}/sr/notifications`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        }),
      (response) => response.json(),
    );
  }

  /** Acknowledge post-completion SR cancel — Indici button action. */
  static async ackSrIndiciCancel(notificationId: string): Promise<{ success: boolean }> {
    return srFetch(
      'notifications_ack',
      () =>
        this.fetchWithAuth(`${this.baseUrl}/sr/notifications/${notificationId}/ack-indici`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        }),
      (response) => response.json(),
    );
  }

  /**
   * Reprocess a referral to re-extract information from email and recreate PDFs
   */
  static async reprocessReferral(referralId: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await this.fetchWithAuth(`${this.baseUrl}/referrals/${referralId}/reprocess`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error reprocessing referral:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to reprocess referral');
    }
  }

  /**
   * AXIS166: Get all comments for a referral
   */
  static async getReferralComments(referralId: string): Promise<ReferralComment[]> {
    try {
      const response = await this.fetchWithAuth(`${this.baseUrl}/referrals/${referralId}/comments`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching referral comments:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to fetch comments');
    }
  }

  /**
   * AXIS166: Create a new comment on a referral
   */
  static async createReferralComment(referralId: string, body: string): Promise<ReferralComment> {
    try {
      const response = await this.fetchWithAuth(`${this.baseUrl}/referrals/${referralId}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ body }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error creating referral comment:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to create comment');
    }
  }

  /**
   * AXIS207: Update a referral comment
   */
  static async updateReferralComment(referralId: string, commentId: string, body: string): Promise<ReferralComment> {
    try {
      const response = await this.fetchWithAuth(`${this.baseUrl}/referrals/${referralId}/comments/${commentId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ body }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error updating referral comment:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to update comment');
    }
  }

  /**
   * AXIS207: Delete a referral comment
   */
  static async deleteReferralComment(referralId: string, commentId: string): Promise<void> {
    try {
      const response = await this.fetchWithAuth(`${this.baseUrl}/referrals/${referralId}/comments/${commentId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error deleting referral comment:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to delete comment');
    }
  }

  /**
   * AXISREF252: Export referrals as a CSV file with field selection, date range, and status filters.
   * Returns the CSV blob for direct browser download — no server-side storage.
   */
  static async exportReferralsCsv(params: {
    fields: string[];
    dateFrom?: string;
    dateTo?: string;
    statuses?: string[];
  }): Promise<{ blob: Blob; filename: string }> {
    const query = new URLSearchParams();
    query.set('fields', params.fields.join(','));
    if (params.dateFrom) query.set('date_from', params.dateFrom);
    if (params.dateTo) query.set('date_to', params.dateTo);
    if (params.statuses && params.statuses.length > 0) {
      query.set('statuses', params.statuses.join(','));
    }

    const response = await this.fetchWithAuth(
      `${this.baseUrl}/referrals/export?${query.toString()}`
    );

    if (response.status === 403) {
      throw new Error('You do not have permission to export referral data.');
    }
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Export failed (${response.status})`);
    }

    const blob = await response.blob();
    const disposition = response.headers.get('Content-Disposition') || '';
    const filenameMatch = disposition.match(/filename="([^"]+)"/);
    const filename = filenameMatch ? filenameMatch[1] : 'referrals-export.csv';

    return { blob, filename };
  }

  /**
   * AXIS210: Approve a Miscellaneous referral for triage (promote to AI triage pipeline)
   */
  static async approveForTriage(
    referralId: string,
    note?: string
  ): Promise<{ success: boolean; message: string; referral: Referral }> {
    try {
      const response = await this.fetchWithAuth(
        `${this.baseUrl}/referrals/${referralId}/approve-for-triage`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ note }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error approving for triage:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to approve for triage');
    }
  }
}
